import React, { useState } from 'react';
import { Plus, Trash2, Wand2, AlertTriangle } from 'lucide-react';
import OpenAI from 'openai';

const openai = new OpenAI({
  apiKey: import.meta.env.VITE_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true
});

const APPLIANCE_CATEGORIES = [
  {
    name: 'Kitchen Appliances',
    items: ['Refrigerators', 'Dishwashers', 'Ovens', 'Ranges', 'Microwaves', 'Garbage Disposals']
  },
  {
    name: 'Laundry Appliances',
    items: ['Washers', 'Dryers', 'Washer/Dryer Combos']
  },
  {
    name: 'HVAC',
    items: ['Air Conditioners', 'Heaters', 'Heat Pumps', 'Ventilation Systems']
  },
  {
    name: 'Other Appliances',
    items: ['Water Heaters', 'Trash Compactors', 'Wine Coolers', 'Ice Makers']
  }
];

const APPLIANCE_BRANDS = [
  'LG',
  'Samsung',
  'Whirlpool',
  'GE',
  'Maytag',
  'KitchenAid',
  'Frigidaire',
  'Bosch',
  'Sub-Zero',
  'Wolf',
  'Viking',
  'Miele',
  'Thermador',
  'JennAir',
  'Dacor',
  'Fisher & Paykel',
  'Speed Queen',
  'Electrolux',
  'Amana',
  'Kenmore'
];

interface ServicesEditorProps {
  data: any;
  onChange: (data: any) => void;
}

const ServicesEditor: React.FC<ServicesEditorProps> = ({ data, onChange }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generateServiceDescription = async (service: string) => {
    setLoading(true);
    setError(null);

    try {
      const completion = await openai.chat.completions.create({
        model: "gpt-3.5-turbo",
        messages: [
          {
            role: "system",
            content: "You are a professional appliance repair service copywriter. Write compelling and concise service descriptions."
          },
          {
            role: "user",
            content: `Write a brief, professional description (max 100 characters) for ${service} repair service.`
          }
        ]
      });

      return completion.choices[0]?.message?.content || '';
    } catch (err) {
      console.error('Error generating description:', err);
      setError('Failed to generate description. Please try again.');
      return '';
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = async () => {
    const newCategory = {
      name: '',
      description: '',
      icon: 'Tool',
      items: [],
      featured: false
    };

    onChange({
      ...data,
      services: {
        ...data.services,
        categories: [...(data.services.categories || []), newCategory]
      }
    });
  };

  const handleAddItem = async (categoryIndex: number) => {
    const newItem = {
      name: '',
      description: '',
      brands: [],
      featured: false
    };

    const updatedCategories = [...data.services.categories];
    updatedCategories[categoryIndex].items.push(newItem);

    onChange({
      ...data,
      services: {
        ...data.services,
        categories: updatedCategories
      }
    });
  };

  const handleRemoveCategory = (index: number) => {
    const updatedCategories = data.services.categories.filter((_: any, i: number) => i !== index);
    onChange({
      ...data,
      services: {
        ...data.services,
        categories: updatedCategories
      }
    });
  };

  const handleRemoveItem = (categoryIndex: number, itemIndex: number) => {
    const updatedCategories = [...data.services.categories];
    updatedCategories[categoryIndex].items = updatedCategories[categoryIndex].items.filter(
      (_: any, i: number) => i !== itemIndex
    );

    onChange({
      ...data,
      services: {
        ...data.services,
        categories: updatedCategories
      }
    });
  };

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-medium text-gray-900">Service Categories</h2>
        <button
          onClick={handleAddCategory}
          className="flex items-center px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4 mr-1" />
          Add Category
        </button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 text-red-800 rounded-lg flex items-start">
          <AlertTriangle className="w-5 h-5 mr-2 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="space-y-6">
        {(data.services.categories || []).map((category: any, categoryIndex: number) => (
          <div key={categoryIndex} className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1 mr-4">
                <input
                  type="text"
                  value={category.name}
                  onChange={(e) => {
                    const updatedCategories = [...data.services.categories];
                    updatedCategories[categoryIndex].name = e.target.value;
                    onChange({
                      ...data,
                      services: {
                        ...data.services,
                        categories: updatedCategories
                      }
                    });
                  }}
                  placeholder="Category Name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <textarea
                  value={category.description}
                  onChange={(e) => {
                    const updatedCategories = [...data.services.categories];
                    updatedCategories[categoryIndex].description = e.target.value;
                    onChange({
                      ...data,
                      services: {
                        ...data.services,
                        categories: updatedCategories
                      }
                    });
                  }}
                  placeholder="Category Description"
                  className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={2}
                />
                <div className="mt-2">
                  <label className="inline-flex items-center">
                    <input
                      type="checkbox"
                      checked={category.featured || false}
                      onChange={(e) => {
                        const updatedCategories = [...data.services.categories];
                        updatedCategories[categoryIndex].featured = e.target.checked;
                        onChange({
                          ...data,
                          services: {
                            ...data.services,
                            categories: updatedCategories
                          }
                        });
                      }}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-600">Featured Category</span>
                  </label>
                </div>
              </div>
              <button
                onClick={() => handleRemoveCategory(categoryIndex)}
                className="p-2 text-gray-400 hover:text-red-500"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-sm font-medium text-gray-700">Services</h3>
                <button
                  onClick={() => handleAddItem(categoryIndex)}
                  className="flex items-center px-2 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Service
                </button>
              </div>

              <div className="space-y-3">
                {category.items.map((item: any, itemIndex: number) => (
                  <div key={itemIndex} className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex justify-between items-start">
                      <div className="flex-1 mr-4 space-y-2">
                        <input
                          type="text"
                          value={item.name}
                          onChange={(e) => {
                            const updatedCategories = [...data.services.categories];
                            updatedCategories[categoryIndex].items[itemIndex].name = e.target.value;
                            onChange({
                              ...data,
                              services: {
                                ...data.services,
                                categories: updatedCategories
                              }
                            });
                          }}
                          placeholder="Service Name"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <div className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={item.description}
                            onChange={(e) => {
                              const updatedCategories = [...data.services.categories];
                              updatedCategories[categoryIndex].items[itemIndex].description = e.target.value;
                              onChange({
                                ...data,
                                services: {
                                  ...data.services,
                                  categories: updatedCategories
                                }
                              });
                            }}
                            placeholder="Service Description"
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          <button
                            onClick={async () => {
                              const description = await generateServiceDescription(item.name);
                              if (description) {
                                const updatedCategories = [...data.services.categories];
                                updatedCategories[categoryIndex].items[itemIndex].description = description;
                                onChange({
                                  ...data,
                                  services: {
                                    ...data.services,
                                    categories: updatedCategories
                                  }
                                });
                              }
                            }}
                            disabled={loading}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                            title="Generate description with AI"
                          >
                            <Wand2 className="w-5 h-5" />
                          </button>
                        </div>
                        <select
                          multiple
                          value={item.brands || []}
                          onChange={(e) => {
                            const selectedBrands = Array.from(e.target.selectedOptions, option => option.value);
                            const updatedCategories = [...data.services.categories];
                            updatedCategories[categoryIndex].items[itemIndex].brands = selectedBrands;
                            onChange({
                              ...data,
                              services: {
                                ...data.services,
                                categories: updatedCategories
                              }
                            });
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          size={5}
                        >
                          {APPLIANCE_BRANDS.map(brand => (
                            <option key={brand} value={brand}>{brand}</option>
                          ))}
                        </select>
                        <div className="mt-2">
                          <label className="inline-flex items-center">
                            <input
                              type="checkbox"
                              checked={item.featured || false}
                              onChange={(e) => {
                                const updatedCategories = [...data.services.categories];
                                updatedCategories[categoryIndex].items[itemIndex].featured = e.target.checked;
                                onChange({
                                  ...data,
                                  services: {
                                    ...data.services,
                                    categories: updatedCategories
                                  }
                                });
                              }}
                              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <span className="ml-2 text-sm text-gray-600">Featured Service</span>
                          </label>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveItem(categoryIndex, itemIndex)}
                        className="p-2 text-gray-400 hover:text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Add</h3>
        <div className="grid grid-cols-2 gap-4">
          {APPLIANCE_CATEGORIES.map((category, index) => (
            <button
              key={index}
              onClick={() => {
                const newCategory = {
                  name: category.name,
                  description: `Professional ${category.name.toLowerCase()} repair and maintenance services`,
                  icon: 'Tool',
                  featured: false,
                  items: category.items.map(item => ({
                    name: item,
                    description: `Expert ${item.toLowerCase()} repair and service`,
                    brands: [],
                    featured: false
                  }))
                };

                onChange({
                  ...data,
                  services: {
                    ...data.services,
                    categories: [...data.services.categories, newCategory]
                  }
                });
              }}
              className="p-4 text-left bg-gray-50 rounded-lg hover:bg-gray-100"
            >
              <h4 className="font-medium text-gray-900">{category.name}</h4>
              <p className="text-sm text-gray-500 mt-1">
                {category.items.length} services
              </p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ServicesEditor;